import styled, { css, keyframes } from 'styled-components';
import { ImageOverlay } from 'pages/charterQuote/components/contents/Content.style';

export const colors = {
  navy: '#002147',
  blue: '#002147',
  blueBright: '#007BFF',
  sky: '#e8f4fc',
  surface: '#F8F9FA',
  white: '#ffffff',
  muted: '#5c6b7a',
  border: '#e9ecef',
};

const sectionPadding = css`
  padding: 100rem 50rem;
`;

export const PageRoot = styled.div`
  font-family: 'Inter', 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
  color: ${colors.navy};
  background: ${colors.white};
  overflow-x: hidden;
`;

const wcTickerMarqueeScroll = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
`;

export const WcTickerStrip = styled.section`
  /* Clear fixed Layout header (~.header-container height vw(64) ≈ 64rem here) — otherwise the strip renders under it. */
  margin-top: 64rem;
  background: #155dfc;
  border-bottom: 1px solid rgba(255, 255, 255, 0.22);
  position: relative;
  color: ${colors.white};
`;

export const WcTickerAccessibleText = styled.p`
  margin: 0;
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;

  @media (prefers-reduced-motion: reduce) {
    position: static;
    width: auto;
    height: auto;
    margin: 0;
    overflow: visible;
    clip: auto;
    white-space: normal;
    padding: 14px clamp(16px, 4vw, 48px);
    font-size: 14px;
    line-height: 1.45;
    color: rgba(255, 255, 255, 0.98);
    text-align: center;
  }
`;

export const WcTickerLabel = styled.span`
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: 0.5em;
  color: inherit;
`;

export const WcTickerMarquee = styled.div`
  overflow: hidden;
  width: 100%;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export const WcTickerTrack = styled.div`
  display: flex;
  width: max-content;
  animation: ${wcTickerMarqueeScroll} 52s linear infinite;

  &:hover {
    animation-play-state: paused;
  }
`;

export const WcTickerSegment = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 11px clamp(28px, 8vw, 100px);
  font-size: 14px;
  line-height: 1.35;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.97);
  white-space: nowrap;
`;

export const WcFlyingCloudsBanner = styled.img`
  display: block;
  width: 100vw;
  max-width: 100vw;
  object-fit: cover;
`;

export const HeroWrap = styled.section`
  position: relative;
  min-height: min(92vh, 840px);
  display: flex;
  flex-direction: column;
  color: ${colors.white};
  background: #020814 url('/imgs/webp/worldcup2026/top-banner.png') center center / cover no-repeat;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      rgba(0, 12, 32, 0.94) 0%,
      rgba(0, 33, 71, 0.78) 42%,
      rgba(0, 33, 71, 0.35) 58%,
      rgba(0, 33, 71, 0.05) 72%,
      transparent 100%
    );
  }
`;

export const HeroWrapCharter = styled(HeroWrap)`
  color: #07080a;
  background: ${colors.surface};

  &::before {
    display: none;
  }
`;

export const WcQuoteHeroBand = styled.div`
  flex: 1 0 auto;
  width: 100%;
  background-color: ${colors.white};
  color: #07080a;
`;

export const WcQuoteHeroHeader = styled.header`
  box-sizing: border-box;
  width: min(1200px, 100%);
  margin: 0 auto;
  padding: 150rem 0 0 50rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: stretch;
  min-height: 715px;

  @media (max-width: 1024px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: none;
    gap: 28px;
    min-height: 0;
    padding: 150rem 0 100rem 100rem;
  }
`;

export const WcQuoteHeroResultsTop = styled.nav`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  grid-column: 1;
  grid-row: 1;
  min-height: 715px;

  @media (max-width: 1024px) {
    grid-column: auto;
    grid-row: auto;
    min-height: 0;
  }
`;

export const WcQuoteHeroMainWrap = styled.div`
  flex: 0 0 auto;
`;

export const WcQuoteHeroTitleBlock = styled.div`
  margin-bottom: 48px;

  @media (max-width: 1024px) {
    margin-bottom: 20px;
  }
`;

export const WcQuoteHeroHeading = styled.h1`
  font-weight: 500;
  font-size: clamp(32px, 4vw, 48px);
  color: #050a30;
  margin: 0;
  line-height: 1.1;
  max-width: 560px;

  @media (max-width: 1024px) {
    font-size: clamp(28px, 6vw, 44px);
  }
`;

export const WcQuoteHeroLead = styled.p`
  font-weight: 400;
  font-size: 16px;
  color: #4a5565;
  margin: 48px 0 0;
  line-height: 1.4;
  max-width: 560px;
`;

export const WcQuoteHeroBadgeGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 11px;
  margin-top: 48px;
  max-width: 540px;

  @media (max-width: 520px) {
    max-width: 100%;
  }
`;

export const WcQuoteHeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 18px;
  border-radius: 32px;
  background: #eff6ff;
  color: #1447e6;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.25;
  letter-spacing: 0.01em;
`;

export const WcQuoteHeroActionSlot = styled.div`
  display: flex;
  justify-content: flex-start;
  z-index: 1;
  margin-top: 28px;
  padding-top: 0;
`;

export const WcQuoteHeroPrimaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px 28px;
  border-radius: 8px;
  background: #155dfc;
  color: ${colors.white};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.2;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: background 0.18s ease, transform 0.15s ease, box-shadow 0.18s ease;
  box-shadow: unset;

  &:hover {
    background: #006adf;
    color: ${colors.white};
    transform: translateY(-1px);
  }

  svg {
    flex-shrink: 0;
  }
`;

export const WcQuoteHeroBanner = styled.img`
  position: absolute;
  width: auto;
  height: auto;
  right: 0;
  top: 100rem;
  z-index: 0;
  display: block;
  pointer-events: none;

  width: 100%;
  max-width: 40%;
  min-height: 280px;
  max-height: 600px;
  object-fit: cover;
  object-position: center;
  border-radius: 2px;
  pointer-events: auto;
`;

export const BtnPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 28px;
  border-radius: 8px;
  background: ${colors.white};
  color: ${colors.navy};
  font-weight: 700;
  font-size: 15px;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.25);
    color: ${colors.navy};
    opacity: 0.95;
  }
`;

export const BtnHeroDark = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 28px;
  border-radius: 8px;
  background: ${colors.navy};
  color: ${colors.white};
  font-weight: 700;
  font-size: 15px;
  text-decoration: none;
  border: 2px solid rgba(255, 255, 255, 0.35);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    background: #001a38;
    color: ${colors.white};
  }
`;

/** Screen-reader text for banner background image (CSS bg has no native alt). */
export const WcPlanningBannerBgAlt = styled.span`
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
`;

/** World Cup planning CTA: wrap raw ImageOverlay — matches emptyLeg / charter quote usage */
export const WcPlanningBannerWrap = styled.div`
  width: 100%;
  border-radius: 0;
  overflow: hidden;

  ${ImageOverlay} {
    position: relative;
    min-height: clamp(300px, 40vw, 320px);
    padding: clamp(36px, 6vw, 56px) clamp(24px, 4vw, 40px);
    gap: clamp(14px, 2vw, 20px);

    &::before {
      background-color: rgba(0, 0, 0, 0.55);
    }

    ${WcPlanningBannerBgAlt} {
      position: absolute !important;
      left: 0;
      top: 0;
      z-index: 2;
    }

    h2 {
      margin: 0;
      font-size: clamp(26px, 4vw, 28px);
      font-weight: 600;
      line-height: 1.18;
      color: #fff !important;
    }

    .wc-planning-lead {
      margin: 0 auto;
      max-width: 640px;
      font-size: clamp(14px, 2vw, 17px);
      line-height: 1.55;
      font-weight: 400;
      color: rgba(255, 255, 255, 0.95);
    }

    .wc-planning-cta-row {
      display: flex;
      justify-content: center;
      width: 100%;
      margin-top: 6px;

      a {
        display: inline-flex;
        align-items: center;
        gap: 10px;
      }
    }
  }
`;

export const WcPlanningBannerSection = styled.section`
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  margin: 0;
  padding: clamp(24px, 4vw, 48px) 0;
`;

export const Section = styled.section`
  ${sectionPadding};
  max-width: 1300px;
  margin: 0rem auto;
  width: 100%;
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  text-align: center;
  font-size: clamp(24px, 3vw, 34px);
  font-weight: 700;
  color: ${colors.navy};
  line-height: 1.2;
`;

const SectionLead = styled.p`
  margin: 0 auto 40px;
  max-width: 760px;
  text-align: center;
  color: ${colors.muted};
  font-size: 16px;
  line-height: 1.6;
`;

const HostAirportIntro = styled.div`
  margin-bottom: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 900px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: clamp(24px, 5vw, 56px);
  }
`;

const HostAirportTitle = styled(SectionTitle)`
  margin: 0;
  flex: 1 1 48%;
  min-width: min(420px, 100%);
  text-align: left;
`;

const HostAirportLead = styled(SectionLead)`
  margin: 0;
  max-width: none;
  flex: 1 1 44%;
  min-width: min(340px, 100%);
  text-align: left;
  font-size: 15px;
  line-height: 1.6;
`;

export const GroupCharterSplashTitle = styled(HostAirportTitle)`
  font-size: clamp(26px, 3.6vw, 40px);
  line-height: 1.18;
`;

export const GroupCharterSplashLead = styled(HostAirportLead)`
  font-size: clamp(14px, 1.6vw, 16px);
  color: ${colors.muted};
`;

export const GroupCharterSplashIntro = styled(HostAirportIntro)`
  margin-bottom: 0;
`;

export const GroupCharterSplashDivider = styled.div`
  height: 1px;
  background: ${colors.border};
  margin: clamp(22px, 3.5vw, 34px) 0 0;
`;

export const WcFaqTwinStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(36px, 5vw, 56px);
  width: 100%;
`;

export const WcFaqTwinRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: 16px;

  @media (min-width: 900px) {
    flex-direction: row;
    align-items: stretch;
    min-height: min(560px, 70vh);
  }
`;

export const WcFaqTwinFigure = styled.figure`
  margin: 0;
  position: relative;
  flex: 1 1 50%;
  min-height: min(260px, 52vw);
  background: #0b1224;
  overflow: hidden;

  @media (min-width: 900px) {
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  img {
    width: 100%;
    flex: 1;
    min-height: 220px;
    object-fit: cover;
    display: block;
  }
`;

export const WcFaqTwinPanel = styled.div`
  flex: 1 1 50%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: #f4f6f8;
  padding: clamp(22px, 3vw, 34px);
  justify-content: space-between;
`;

export const WcFaqTwinHeading = styled.h2`
  margin: 0 0 8px;
  font-size: clamp(22px, 2.6vw, 30px);
  font-weight: 700;
  color: ${colors.navy};
  line-height: 1.22;
`;

export const WhyPrivateFleetSection = styled(Section)`
  display: flex;
  flex-direction: column;
  gap: clamp(36px, 5vw, 50px);
  /* Charter Header(H2Text/NormalSpan) 依赖的全局自定义属性 */
  --normal-h2-h3-color: #050a30;
  --normal-color: #424748;
`;

export const WhyPrivateBenefitsGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  align-items: stretch;
  gap: 16px;
  grid-auto-rows: 1fr;

  @media (min-width: 1100px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

export const WhyPrivateBenefitCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #f4f6fc;
  padding: clamp(16px, 2.5vw, 28px);
  min-height: 300px;
  height: 100%;
  border-radius: 8px;
  box-sizing: border-box;
`;

export const WhyPrivateBenefitTitle = styled.h3`
  margin: 0;
  font-size: 18px !important;
  font-weight: 600;
  color: ${colors.navy};
  line-height: normal;
`;

export const WhyPrivateBenefitBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const WhyPrivateBenefitText = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.55;
  color: ${colors.muted};
`;

export const TableWrap = styled.div`
  overflow-x: auto;
  margin-top: 64px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: ${colors.white};
  display: none;
  @media (min-width: 900px) {
    display: block;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  thead th {
    background: #f3f4f6;
    color: #101828;
    text-align: left;
    padding: 14px 16px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    border-bottom: 1px solid #e5e7eb;
  }

  tbody tr:nth-child(even) {
    background: #eff6ff;
  }
  tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: top;
    color: #334155;
  }
`;

/** Dedicated table styling for FIFA host-airport roster (muted header, knockout row tint) */
export const HostAirportTable = styled(StyledTable)`
  thead th {
    background: #f1f5f9;
    color: ${colors.navy};
    cursor: pointer;
    border-bottom: 1px solid #e5e7eb;
    white-space: normal;
    line-height: 1.35;
  }

  tbody tr:nth-child(even),
  tbody tr:nth-child(odd) {
    background: ${colors.white};
  }

  tbody tr.highlight-row {
    background: #eff6ff;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

export const StageTag = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;

  ${(p) => {
    switch (p.$tone) {
      case 'final':
        return css`
          background: #dbeafe;
          color: #193cb8;
        `;
      case 'semifinal':
        return css`
          background: #f3e8ff;
          color: #6e11b0;
        `;
      case 'quarterfinal':
        return css`
          background: #dcfce7;
          color: #016630;
        `;
      default:
        return css`
          background: #f3f4f6;
          color: #1e2939;
        `;
    }
  }}
`;

export const MobileTableAccordion = styled.div`
  display: block;
  @media (min-width: 900px) {
    display: none;
  }
`;

export const WcPricingInteractive = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 28px;
  align-items: stretch;
  margin-top: 80rem;

  @media (min-width: 960px) {
    flex-direction: row;
    align-items: stretch;
    gap: clamp(28px, 4vw, 52px);
  }
`;

export const WcPricingCardColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 44%;
  min-width: min(460px, 100%);
`;

export const WcPricingTierBtn = styled.button.attrs({ type: 'button' })`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  text-align: left;
  cursor: pointer;
  border: none;
  border-radius: 6px;
  padding: clamp(14px, 2vw, 18px) clamp(16px, 2.5vw, 20px);
  font-family: inherit;
  transition: background-color 0.25s ease, color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;

  ${(props) =>
    props.$active
      ? css`
          background: #0063db;
          color: ${colors.white};
          box-shadow: 0 4px 14px rgba(0, 123, 255, 0.28);
          transform: translateY(-2px);
        `
      : css`
          background: #f4f6fc;
          color: ${colors.navy};
          box-shadow: none;
          transform: translateY(0);
          &:hover {
            background: #ebeef5;
          }
        `};

  &:focus-visible {
    outline: 3px solid rgba(0, 123, 255, 0.45);
    outline-offset: 2px;
  }
`;

export const WcPricingTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: baseline;
`;

export const WcPricingTierHeading = styled.span`
  font-size: clamp(16px, 1.85vw, 18px);
  font-weight: 700;
  line-height: 1.35;
  color: ${(p) => (p.$active ? colors.white : colors.navy)};
`;

export const WcPricingTierPriceStr = styled.span`
  flex-shrink: 0;
  font-size: clamp(15px, 1.85vw, 18px);
  font-weight: 700;
  line-height: 1.35;
  color: ${(p) => (p.$active ? colors.white : '#1e293b')};
`;

export const WcPricingTierSubtitle = styled.div`
  margin-top: 6px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  color: ${(p) => (p.$active ? 'rgba(255, 255, 255, 0.88)' : 'rgba(71, 85, 105, 0.95)')};
`;

export const WcPricingTierBody = styled.p`
  margin: 32px 0 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: ${(p) => (p.$active ? 'rgba(255, 255, 255, 0.93)' : colors.muted)};
`;

export const WcPricingVisual = styled.figure`
  margin: 0;
  flex: 1 1 48%;
  min-height: min(340px, 52vw);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: min(100%, 480px);
    max-height: 280px;
    height: auto;
    object-fit: contain;
    display: block;
    filter: drop-shadow(12px 16px 20px rgba(15, 23, 42, 0.12));
  }

  @media (max-width: 959px) {
    min-height: 240px;

    img {
      max-height: 200px;
    }
  }
`;

export const WcPricingTournamentAlert = styled.p`
  width: 100%;
  box-sizing: border-box;
  margin: clamp(28px, 5vw, 48px) 0 0;
  padding: clamp(16px, 3vw, 22px) clamp(18px, 3vw, 24px);
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  font-size: 14px;
  line-height: 1.55;
  color: #78350f;

  strong {
    font-weight: 700;
    color: #92400e;
  }
`;

export const WcAirportBriefCosts = styled.div`
  width: 100%;
  margin-top: 38px;
  display: flex;
  flex-direction: column;
`;

export const WcAirportBriefGridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  border-bottom: 1px solid #dde2e4;

  @media (min-width: 780px) {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
`;

export const WcAirportBriefGhost = styled.div`
  display: none;
  @media (min-width: 780px) {
    display: block;
  }
`;

export const WcAirportBriefCell = styled.article`
  padding: clamp(18px, 3vw, 24px) 8px;
  max-width: 100%;
  box-sizing: border-box;
`;

export const WcAirportBriefFboCell = styled(WcAirportBriefCell)`
  @media (min-width: 780px) {
    grid-column: 1 / -1;
  }
`;

export const WcAirportBriefStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
`;

export const WcAirportBriefHeadRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

export const WcAirportBriefBlockTitle = styled.h3`
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 18px !important;
  font-weight: 700;
  line-height: 1.3;
  color: ${colors.navy};
`;

export const WcAirportBriefRailImg = styled.img`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 2px;
  object-fit: contain;
`;

export const WcAirportBriefRailIcon = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #07080a;

  svg {
    display: block;
  }
`;

export const WcAirportBriefText = styled.p`
  margin: 0;
  padding-left: calc(24px + 12px);
  font-size: 15px;
  font-weight: 400;
  line-height: 1.55;
  color: ${colors.muted};
`;

export const WcAirportBriefBulletList = styled.ul`
  margin: 0;
  padding-left: calc(24px + 12px + 1rem);
  font-size: 15px;
  line-height: 1.55;
  color: ${colors.muted};

  li + li {
    margin-top: 8px;
  }
`;

const WC_SPLASH_BLUE = '#1d63d2';

export const WcRegSplashSplit = styled.div`
  width: 100%;
  margin-top: 42px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;
  align-items: start;

  @media (min-width: 960px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr;
    align-items: stretch;
    gap: clamp(28px, 4vw, 48px);
  }
`;

/** Split below intro bar — spacing tuned after divider line */
export const GroupCharterSplashSplit = styled(WcRegSplashSplit)`
  margin-top: clamp(24px, 3.5vw, 38px);
`;

export const WcRegSplashFigure = styled.figure`
  margin: 0;
  border-radius: 12px;
  overflow: hidden;
  background: #0b1224;
  display: flex;
  flex-direction: column;
  min-height: min(320px, 52vw);

  img {
    display: block;
    width: 100%;
    flex: 1;
    object-fit: cover;
    object-position: center 40%;
  }

  @media (min-width: 960px) {
    height: 100%;
    min-height: 0;
  }
`;

export const WcRegSplashAccordion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-height: min(280px, 48vw);

  @media (min-width: 960px) {
    height: 100%;
    min-height: 0;
  }
`;

export const WcRegSplashAccordionSpacer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  pointer-events: none;
  display: none;

  @media (min-width: 960px) {
    display: block;
  }
`;

export const WcRegSplashItem = styled.article`
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  background: ${(p) => (p.$open ? WC_SPLASH_BLUE : '#f4f6f8')};
  color: ${(p) => (p.$open ? '#ffffff' : colors.navy)};
  transition: background 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  ${(p) =>
    p.$open
      ? css`
          box-shadow: 0 10px 28px rgba(29, 99, 210, 0.28);
        `
      : css`
          box-shadow: 0 1px 0 rgba(15, 23, 42, 0.04);
        `}
`;

export const WcRegSplashHead = styled.button.attrs({ type: 'button' })`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  color: inherit;

  &:focus-visible {
    outline: 3px solid rgba(29, 99, 210, 0.45);
    outline-offset: 2px;
  }
`;

export const WcRegSplashHeadMain = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

export const WcRegSplashNumPlain = styled.span`
  flex-shrink: 0;
  width: 32px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #64748b;
`;

export const WcRegSplashNumBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  background: ${colors.white};
  color: ${WC_SPLASH_BLUE};
  font-weight: 800;
  font-size: 13px;
  border-radius: 4px;
`;

export const WcRegSplashChevron = styled.span.attrs({ 'aria-hidden': true })`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: currentColor;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease;
  opacity: ${(p) => (p.$open ? 0.95 : 0.72)};
  transform: ${(p) => (p.$open ? 'rotate(-180deg)' : 'rotate(0deg)')};

  svg {
    display: block;
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0.01ms;
  }
`;

/** Smooth reveal without mount/unmount (grid 0fr → 1fr) */
export const WcRegSplashCollapse = styled.div`
  display: grid;
  grid-template-rows: ${(p) => (p.$open ? '1fr' : '0fr')};
  transition: grid-template-rows 0.48s cubic-bezier(0.4, 0, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0.01ms;
  }
`;

export const WcRegSplashCollapseInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;

export const WcRegSplashBody = styled.div`
  padding: 0 18px 18px;
  border-top: 1px solid ${(p) => (p.$open ? 'rgba(255, 255, 255, 0.22)' : 'transparent')};
  transition: border-top-color 0.25s ease;
`;

export const WcRegSplashBodyText = styled.p`
  margin: 0;
  padding: 14px 0 0 calc(32px + 12px);
  font-size: 15px;
  font-weight: 400;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.94);
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: ${(p) => (p.$visible ? 'translateY(0)' : 'translateY(-6px)')};
  transition: opacity 0.38s cubic-bezier(0.4, 0, 0.2, 1) ${(p) => (p.$visible ? '0.04s' : '0ms')},
    transform 0.42s cubic-bezier(0.4, 0, 0.2, 1) ${(p) => (p.$visible ? '0.04s' : '0ms')};

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition-duration: 0.01ms;
    transition-delay: 0ms;
  }
`;

export const BottomCta = styled.section`
  padding: 80px 24px;
  text-align: center;
  color: ${colors.white};
  background: url('/imgs/webp/worldcup/footer.webp') center / cover no-repeat;

  h2 {
    margin: 0 0 16px;
    font-size: clamp(22px, 3vw, 30px);
    font-weight: 600;
    max-width: 1000px;
    margin-left: auto;
    margin-right: auto;
    color: white;
  }
  p {
    margin: 0 auto 24px;
    max-width: 800px;
    font-size: 16px;
    line-height: 1.65;
    opacity: 0.94;
  }
`;

export const StickyMobileCta = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  background: rgba(255, 255, 255, 0.98);
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 10px;
  justify-content: center;
  box-shadow: 0 -4px 20px rgba(5, 10, 48, 0.12);

  @media (min-width: 900px) {
    display: none;
  }

  a {
    flex: 1;
    text-align: center;
  }
`;
